/**
 * The media endpoints.
 *
 * Media is partitioned by owner: one partition per store, plus the shared
 * `GLOBAL` library that every brand can draw on. That is a storage decision, but
 * it surfaces here because a listing is one Query against one partition — so
 * `listMedia` takes a single scope, and a page showing several scopes at once
 * asks for each of them and merges. Six small requests beat one that has to scan.
 *
 * Every listing is filtered to the scope that was asked for after it comes back.
 * That looks redundant against a server that honours the query parameter, and it
 * is — but it is also what makes the page correct if the route ever answers with
 * everything the caller may reach instead, which is how the fleet listing
 * behaves. The cost is a predicate; the alternative is a library page quietly
 * showing another store's promos.
 */

import { z } from 'zod';
import type {
  UploadInit,
  UploadInitResponse,
  mediaScopeSchema,
  mediaUpdateSchema,
} from '@pbtv/shared';
import { uploadInitResponseSchema, uploadPartUrlsResponseSchema } from '@pbtv/shared';
import { apiRequest, requestJson } from './api.js';
import { collectionOf, entityOf, withIdAlias } from './envelope.js';

export type MediaScope = z.infer<typeof mediaScopeSchema>;
export type MediaUpdate = z.infer<typeof mediaUpdateSchema>;

/**
 * The shared library's partition sentinel, sent as the scope when there is no
 * store. It is the same literal the table uses for that partition, so a route
 * that compares the parameter against its own key builder agrees with it.
 */
export const SHARED_SCOPE_PARAM = 'GLOBAL';

// ---------------------------------------------------------------------------
// Shapes read back
// ---------------------------------------------------------------------------

/**
 * What the library renders. Read leniently on purpose: `type` is a plain string
 * rather than the manifest's four-way enum so that one asset written by a newer
 * API cannot blank the whole page, and the numeric fields default rather than
 * refuse, because a missing width is a cosmetic problem and a rejected list is
 * not.
 */
export const mediaAssetSchema = withIdAlias(
  z.object({
    assetId: z.string().min(1),
    storeId: z.string().nullable().default(null),
    type: z.string().min(1),
    url: z.string().min(1),
    thumbUrl: z.string().nullable().default(null),
    title: z.string().default(''),
    bytes: z.number().int().nonnegative().default(0),
    width: z.number().int().nonnegative().default(0),
    height: z.number().int().nonnegative().default(0),
    durationMs: z.number().int().nonnegative().nullable().default(null),
    contentExpiresAt: z.string().nullable().default(null),
    brands: z.array(z.string()).default([]),
    createdAt: z.string().default(''),
  }),
  'assetId',
);

export type MediaAsset = z.infer<typeof mediaAssetSchema>;

export const mediaListSchema = collectionOf(mediaAssetSchema, 'media', 'assets');
export const mediaEnvelopeSchema = entityOf(mediaAssetSchema, 'asset', 'media');

/**
 * Just enough of a playlist to answer "is this asset still in use?".
 *
 * The playlist editor owns the real client; this reads the same route through a
 * deliberately narrow schema so that deleting an asset can warn without the two
 * pages sharing a type that either of them might change.
 */
const playlistUsageSchema = withIdAlias(
  z
    .object({
      playlistId: z.string().min(1),
      name: z.string().default(''),
      storeId: z.string().nullable().default(null),
      items: z.array(z.object({ assetId: z.string() }).passthrough()).default([]),
    })
    .passthrough()
    .transform((playlist) => ({
      playlistId: playlist.playlistId,
      name: playlist.name,
      storeId: playlist.storeId,
      assetIds: playlist.items.map((item) => item.assetId),
    })),
  'playlistId',
);

export type PlaylistUsage = z.infer<typeof playlistUsageSchema>;

const playlistUsageListSchema = collectionOf(playlistUsageSchema, 'playlists');

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const MEDIA_QUERY_KEY: readonly string[] = ['admin', 'media'];

/**
 * Filed under playlists rather than under media on purpose: what it answers
 * changes when a playlist changes, so it should be swept by the playlist
 * editor's invalidation and not by an upload.
 */
export const PLAYLIST_USAGE_QUERY_KEY: readonly string[] = ['admin', 'playlists', 'usage'];

export function scopeParam(storeId: string | null): string {
  return storeId ?? SHARED_SCOPE_PARAM;
}

/**
 * The cache key for one scope's listing. Every media key hangs off
 * `MEDIA_QUERY_KEY`, so an upload anywhere invalidates the lot with a single
 * prefix — including the listings the playlist editor holds for its asset
 * picker.
 */
export function mediaScopeQueryKey(storeId: string | null): readonly string[] {
  return [...MEDIA_QUERY_KEY, 'scope', scopeParam(storeId)];
}

export async function listMedia(
  storeId: string | null,
  signal?: AbortSignal,
): Promise<MediaAsset[]> {
  const assets = await apiRequest(
    mediaListSchema,
    `/admin/media?storeId=${encodeURIComponent(scopeParam(storeId))}`,
    { signal },
  );
  return assets.filter((asset) => asset.storeId === storeId);
}

export async function listPlaylistUsage(signal?: AbortSignal): Promise<PlaylistUsage[]> {
  return apiRequest(playlistUsageListSchema, '/admin/playlists', { signal });
}

/** Pure: which playlists would lose an item if this asset went away. */
export function playlistsUsingAsset(
  playlists: readonly PlaylistUsage[],
  assetId: string,
): PlaylistUsage[] {
  return playlists.filter((playlist) => playlist.assetIds.includes(assetId));
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export async function updateMedia(
  storeId: string | null,
  assetId: string,
  patch: MediaUpdate,
): Promise<void> {
  await requestJson(
    `/admin/media/${encodeURIComponent(assetId)}?storeId=${encodeURIComponent(scopeParam(storeId))}`,
    { method: 'PATCH', body: patch },
  );
}

export async function deleteMedia(storeId: string | null, assetId: string): Promise<void> {
  await requestJson(
    `/admin/media/${encodeURIComponent(assetId)}?storeId=${encodeURIComponent(scopeParam(storeId))}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Upload plumbing
//
// These three are the only network calls the multipart client makes to the API;
// the part PUTs go straight to S3. Keeping them here rather than in upload.ts
// leaves the uploader itself free of any knowledge of the panel's fetch client,
// which is what lets it be driven by a fake transport in tests.
// ---------------------------------------------------------------------------

export async function initiateUpload(
  body: UploadInit,
  signal?: AbortSignal,
): Promise<UploadInitResponse> {
  return apiRequest(uploadInitResponseSchema, '/admin/media/uploads', {
    method: 'POST',
    body,
    signal,
  });
}

export interface PartUrl {
  partNumber: number;
  url: string;
}

export async function fetchPartUrls(
  assetId: string,
  partNumbers: readonly number[],
  signal?: AbortSignal,
): Promise<PartUrl[]> {
  const answer = await apiRequest(
    uploadPartUrlsResponseSchema,
    `/admin/media/uploads/${encodeURIComponent(assetId)}/part-urls`,
    { method: 'POST', body: { partNumbers: [...partNumbers] }, signal },
  );
  return answer.urls;
}

export interface CompletedPart {
  partNumber: number;
  eTag: string;
}

export async function completeUpload(
  assetId: string,
  parts: readonly CompletedPart[] | null,
  signal?: AbortSignal,
): Promise<MediaAsset | null> {
  // `parts` is optional in the contract: when a browser could not read the ETag
  // off a part response the server lists the parts itself. Sending an incomplete
  // list would be worse than sending none.
  const body = parts === null ? {} : { parts: [...parts] };
  const payload = await requestJson(
    `/admin/media/uploads/${encodeURIComponent(assetId)}/complete`,
    { method: 'POST', body, signal },
  );

  const parsed = mediaEnvelopeSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}
